import React, { useEffect, useMemo, useState } from 'react';
import {
  isFreshGanttTimelinePeer,
  type GanttTimelinePeerPresence,
} from '../../utils/ganttTimelinePresenceService';
import { projectGanttPeerCursor } from '../../utils/ganttTimelineCursorUtils';
import type { GanttFlatRow } from '../../utils/ganttSubtaskRows';
import './GanttTimelinePresenceLayer.css';

export interface GanttTimelinePresenceProjection {
  flatRows: GanttFlatRow[];
  rangeStartMs: number;
  cellWidth: number;
  scrollLeft: number;
  scrollTop: number;
  leftPanelWidth: number;
  hideLeftPanel: boolean;
  pinnedRowCount: number;
}

interface GanttTimelinePresenceLayerProps {
  selfViewHash: string;
  selfSyncId?: string | null;
  peers: GanttTimelinePeerPresence[];
  projection: GanttTimelinePresenceProjection;
}

const GanttTimelinePresenceLayer: React.FC<GanttTimelinePresenceLayerProps> = ({
  selfViewHash,
  selfSyncId = null,
  peers,
  projection,
}) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const visible = useMemo(() => {
    const now = Date.now();
    return peers
      .filter(
        (p) =>
          isFreshGanttTimelinePeer(p, now)
          && p.rowKey
          && (p.viewHash === selfViewHash || (!!selfSyncId && p.syncId === selfSyncId)),
      )
      .map((p) => {
        const pos = projectGanttPeerCursor({
          rowKey: p.rowKey,
          timeMs: p.timeMs,
          pane: p.pane,
          ...projection,
        });
        return pos ? { ...p, ...pos } : null;
      })
      .filter((x): x is GanttTimelinePeerPresence & { x: number; y: number } => !!x);
  }, [peers, selfViewHash, selfSyncId, projection]);

  if (visible.length === 0) return null;

  return (
    <div className="gantt-presence-layer" aria-hidden>
      {visible.map((c) => (
        <div
          key={c.uid}
          className="gantt-presence-cursor"
          style={{ left: c.x, top: c.y }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" className="gantt-presence-arrow">
            <path
              d="M5.5 3.2L19 11.4l-6.3 1.5-3.2 5.8z"
              fill={c.color}
              stroke="#fff"
              strokeWidth="1.4"
            />
          </svg>
          <span className="gantt-presence-name" style={{ background: c.color }}>
            {c.name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default React.memo(GanttTimelinePresenceLayer);
